import type { PageType } from '../types/page-context.js';
import type { ContextPopupDelayByPageType } from '../types/widget-settings.js';

const PAGE_TYPES: PageType[] = [
  'home',
  'product',
  'category',
  'cart',
  'checkout',
  'blog',
  'search',
  'other',
];

/** Admin alias → canonical `PageType` (e.g. "catalog" for category listing). */
const PAGE_TYPE_ALIASES: Record<string, PageType> = {
  catalog: 'category',
};

function parseDelayValue(value: unknown): number | null {
  const n =
    typeof value === 'number'
      ? value
      : Number.parseInt(String(value ?? ''), 10);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

/**
 * Parse `CONTEXT_POPUP_DELAY_BY_PAGE_TYPE` JSON from settings API.
 * Unknown keys are ignored; `"catalog"` maps to `category`.
 */
export function parseContextPopupDelayByPageType(
  raw: string | undefined,
): ContextPopupDelayByPageType {
  if (!raw?.trim()) return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {};
  }

  const result: ContextPopupDelayByPageType = {};

  for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
    const pageType = PAGE_TYPE_ALIASES[key] ?? (key as PageType);
    if (!PAGE_TYPES.includes(pageType)) continue;

    const delaySeconds = parseDelayValue(value);
    if (delaySeconds === null) continue;

    result[pageType] = delaySeconds;
  }

  return result;
}

/**
 * Resolve proactive popup delay for a page type.
 * Priority: per-type map → `CONTEXT_POPUP_DELAY_SECONDS` fallback.
 */
export function resolveContextPopupDelaySeconds(
  pageType: PageType,
  byPageType: ContextPopupDelayByPageType,
  fallbackSeconds: number,
): number {
  const specific = byPageType[pageType];
  if (specific !== undefined) return specific;
  return fallbackSeconds;
}
